import React, { useState, useEffect, useMemo } from 'react';
import styled, { css } from 'styled-components';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';

import { useAuth } from '../../context/AuthContext';
import Breadcrumbs from '../../components/ui/Breadcrumbs';
import Tabs from '../../components/ui/Tabs';
import TextInput from '../../components/ui/TextInput.jsx';
import CheckboxInput from '../../components/ui/CheckboxInput';
import ConfirmDialog from '../../components/ui/ConfirmDialog';
import TimeInput from '../../components/ui/TimeInput';
import GenericSelect from '../../components/ui/GenericSelect';
import Button from '../../components/ui/Button';
import { usePerformanceSettingsApi, useRatingScalesApi } from './config';
import CompetenciesTab from './CompetenciesTab';
import FeedbackFormsTab from './FeedbackFormsTab';
import NotificationTemplatesTab from './NotificationTemplatesTab';
import RatingScalesTab from './RatingScalesTab';
import GoalTemplatesTab from './GoalTemplatesTab';
import DelegationsTab from './DelegationsTab';
import IntegrationsTab from './IntegrationsTab';
import { DEFAULT_TIME_ZONE_ID, timeZoneOptions } from '../../utils/timeZones';
import { INSIGHT_RULE_DEFAULTS, insightRulesFromJson, insightRulesToJson, insightRulesValid } from './insightRules';

// Task 33 — PM Konfigürasyon sayfası. Sekme 1 (Ayarlar) burada; diğer sekmeler
// ayrı dosyalarda (CompetenciesTab / FeedbackFormsTab / NotificationTemplatesTab /
// RatingScalesTab — Skorlama Plan 2 Task 10, en sona eklendi).
// Yetkinlikler sekmesi 360° Form Şablonları'ndan ÖNCE durur: form soruları bu
// kütüphaneden beslenir, kurulum sırası da bu. Ağırlık toplamı
// canlı sayacı Faz A'daki GoalsPage dip-toplam desenini birebir tekrarlar
// (aynı yeşil/kırmızı ton: #1B7A3E / #B02A37).

const Container = styled.div``;

const Section = styled.div`
  background: #fff;
  border: 1px solid #E8ECF0;
  border-radius: 6px;
  padding: 20px;
  margin-bottom: 18px;
`;

const SectionTitle = styled.h3`
  font-size: ${({ theme }) => theme.metrics.font.size.md};
  color: ${({ theme }) => theme.colors.text.primary};
  font-weight: ${({ theme }) => theme.metrics.font.weight.semiBold};
  margin-bottom: 14px;
`;

// Kartlardaki alanlar bir satırda EN FAZLA 3 tane; 4. ve sonrası alt satıra
// geçer. Kırılım viewport'a değil KARTIN kendi genişliğine bağlı (kenar
// çubuğu açık/kapalı fark etmez): sütun, kartın 3'te birinden dar olamaz
// (→ en fazla 3 sütun) ve `minColPx`'ten de dar olamaz (→ kart daraldıkça
// 2'ye, sonra 1'e iner). `min(…, 100%)` kart `minColPx`'ten darsa tek
// sütunun kartı yatay taşırmasını önler. auto-fill boş sütunları korur:
// tek alanlı kart (Varsayılan Puan Skalası) da 3'te bir genişlikte durur,
// alanlar kartlar arasında hizalı kalır.
const maxThreeColumns = (minColPx, gapPx) => css`
  display: grid;
  gap: ${gapPx}px;
  grid-template-columns: repeat(
    auto-fill,
    minmax(max(min(${minColPx}px, 100%), calc((100% - ${2 * gapPx}px) / 3)), 1fr)
  );
`;

const FlagsGrid = styled.div`
  ${maxThreeColumns(280, 14)}
`;

// Yüzen etiketler (TextInput/GenericSelect/TimeInput) mutlak konumlu; dar
// sütunda alt satıra kırılıp alanın İÇİNE düşüyordu. 304px: en uzun etiket
// (RU "Порог включения нового сотрудника в период (%)", 11px/600 Nunito Sans)
// 283px + solda 14px + sağda köşeyi (4px) aşan 6px pay = 303px — 5 dilin
// hepsi tek satıra sığar. Kart bundan da darsa (≤360px telefon) etiket
// kırılmak yerine "…" ile kesilir.
const FieldsGrid = styled.div`
  ${maxThreeColumns(304, 16)}

  label {
    max-width: calc(100% - 20px);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
`;

// Spec 2026-10-09 §7 — "açık görevlere de uygula" yalnız bu alanlardan biri değiştiğinde anlamlı.
const SLA_FIELDS = ['slaGoalWritingDays', 'slaManagerApprovalDays', 'slaHrApprovalDays', 'slaNominationDays', 'slaNominationApprovalDays', 'slaFeedbackFillDays'];

const HintText = styled.p`
  font-size: 12px;
  color: #6c757d;
  margin-bottom: 14px;
`;

const TotalRow = styled.div`
  display: flex;
  align-items: center;
  padding-top: 4px;
`;

const TotalWeight = styled.span`
  font-size: 14px;
  font-weight: 700;
  color: ${({ $balanced }) => ($balanced ? '#1B7A3E' : '#B02A37')};
`;

const FooterBar = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
  justify-content: flex-end;
`;

const SavedHint = styled.span`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.auxiliar.success};
  font-weight: 600;
`;

const WEIGHT_FIELDS = ['weightSelf', 'weightManager', 'weightPeer', 'weightSubordinate', 'weightSenior'];

// P1 — sessiz saat/SLA hesaplarının yorumlandığı tenant saat dilimi (IANA id).
// Liste bilinçli kısa (desteklenen pazarlar); API'den listede olmayan bir değer
// gelirse seçenek olarak KORUNUR (aşağıda birleşim) — kaydetmek onu bozmaz.
const LIKERT_SCORES = [1, 2, 3, 4, 5];

// PerformanceSettings.LikertAnchorLabelsJson ↔ 5 metin alanı.
// Sözleşme (BE UpdatePerformanceAsync doğrulaması): null VEYA tam 5 boş-olmayan
// etiket — kısmi doldurulmuş durumda kaydetme FE'de engellenir.
function likertLabelsFromJson(json) {
  if (!json) return ['', '', '', '', ''];
  try {
    const parsed = JSON.parse(json);
    if (!Array.isArray(parsed) || parsed.length !== 5) return ['', '', '', '', ''];
    return parsed.map((v) => (typeof v === 'string' ? v : ''));
  } catch {
    return ['', '', '', '', ''];
  }
}

function likertLabelsToJson(labels) {
  const trimmed = labels.map((v) => (v || '').trim());
  return trimmed.every((v) => v === '') ? null : JSON.stringify(trimmed);
}

// Slack Bağlayıcısı Task 4 (spec 2026-08-10-slack-baglayicisi-design.md §4) —
// BE'nin OAuth callback'i HER ZAMAN `/settings/integrations?slack=...`'a
// döner (t1-report.md §2, BE DEĞİŞTİRİLMEDİ — görev talimatı). Bu sayfa iki
// AYRI rotadan render edilir (routes/index.js: `/performance/settings` VE
// `/settings/integrations`, ikisi de AYNI bileşen) — hangi path'ten
// gelindiğine BAKILMAKSIZIN (savunmacı: path VEYA query, ikisi de yeterli)
// başlangıç sekmesi `integrations`'a ayarlanır ki callback dönüşü kullanıcıyı
// doğru sekmede karşılasın.
function computeInitialTab(location) {
  if (location.pathname === '/settings/integrations') return 'integrations';
  if (/(?:^|[?&])slack=/.test(location.search)) return 'integrations';
  return 'general';
}

export default function PerformanceSettingsPage() {
  const { t } = useTranslation();
  const location = useLocation();
  // Faz 1 / Görev 5 (PMB-03) — vekâlet uçları sunucuda enterpriseAdmin
  // kapısına alındı. GET kapsamlı olduğu için 403 DEĞİL, BOŞ sayfa döner:
  // sekme açık bırakılsaydı İK olmayan bir PM.Write kullanıcısı boş bir tablo
  // + çalışır "Yeni" butonu görür, kapıyı ancak formu gönderdikten sonra
  // öğrenirdi. CompCyclesPage.js'in Görev 4'te kurduğu hrVisible deseninin
  // aynısı (canWrite('PM') && isHr — İK rolü ayrımı: İK = AuthContext.isHr).
  const { canWrite, isHr } = useAuth();
  const delegationsVisible = canWrite('PM') && Boolean(isHr);
  const [activeTab, setActiveTab] = useState(() => computeInitialTab(location));
  const { settings, fetching, saving, save } = usePerformanceSettingsApi();
  // Skorlama Plan 2, Task 12 — varsayılan puan skalası seçicisi
  // `useRatingScalesApi`'nin (RatingScalesTab'ın kendi hook'u — GET
  // v1/rating-scales, sayfalanmaz) salt liste kısmını okur; create/update/
  // delete/copy burada kullanılmıyor, sadece `data`/`fetching`.
  const { data: ratingScales } = useRatingScalesApi();
  const [form, setForm] = useState(null);
  // Likert çapa etiketleri form'dan AYRI taslak tutulur (JSON alanı ↔ 5 alan
  // eşlemesi; CompetenciesTab'ın levelsFromJson/levelsToJson deseniyle aynı).
  const [likertLabels, setLikertLabels] = useState(['', '', '', '', '']);
  // Analitik Plan 4 — bulgu eşikleri InsightRulesJson'dan AYRI taslak (yüzde/gün ↔ oran eşlemesi; likert deseni).
  const [insightRules, setInsightRules] = useState(INSIGHT_RULE_DEFAULTS);
  const [savedFlash, setSavedFlash] = useState(false);
  const [applySlaToOpenTasks, setApplySlaToOpenTasks] = useState(false);
  const [applyConfirmOpen, setApplyConfirmOpen] = useState(false);

  useEffect(() => {
    if (settings) {
      setForm(settings);
      setLikertLabels(likertLabelsFromJson(settings.likertAnchorLabelsJson));
      setInsightRules(insightRulesFromJson(settings.insightRulesJson));
    }
  }, [settings]);
  const setInsightField = (key) => (e) => {
    const raw = e.target.value;
    setInsightRules((prev) => ({ ...prev, [key]: raw === '' ? 0 : Number(raw) }));
  };
  const insightRulesOk = insightRulesValid(insightRules);

  const setBoolField = (key) => (val) => setForm((prev) => ({ ...prev, [key]: val }));
  const setNumField = (key) => (e) => {
    const raw = e.target.value;
    setForm((prev) => ({ ...prev, [key]: raw === '' ? 0 : Number(raw) }));
  };
  const setTimeField = (key) => (val) => setForm((prev) => ({ ...prev, [key]: val }));

  const weightsTotal = useMemo(() => {
    if (!form) return 0;
    return WEIGHT_FIELDS.reduce((sum, key) => sum + (Number(form[key]) || 0), 0);
  }, [form]);
  const weightsBalanced = weightsTotal === 100;

  // P1 — saat dilimi seçenekleri: sabit liste + (listede yoksa) API'den gelen
  // mevcut değer korunarak gösterilir.
  const tzOptions = useMemo(() => timeZoneOptions(form?.timeZoneId), [form?.timeZoneId]);

  // Skorlama Plan 2, Task 12 — varsayılan puan skalası seçenekleri: baştaki
  // "— Yok —" seçeneği boş id ('') taşır; onChange bunu null'a çevirip gönderir
  // (GenericSelect'in kendi X/clear butonu zaten null gönderiyor — ikisi de
  // aynı sonuca varır). Skala listesi RatingScalesTab'daki gibi doğrudan
  // `id`/`name` alanlarını taşıdığından mapleme gerekmiyor.
  const scaleOptions = useMemo(() => (
    [{ id: '', name: t('performance.settings.defaultScale.noneOption') }, ...ratingScales]
  ), [ratingScales, t]);

  // P1 — birleşik skor ağırlıkları: bayrak açıkken toplam 100 (BE:
  // performance.settings.combinedWeightsMustSum100 ile aynı kural, önleyici).
  const combinedTotal = (Number(form?.combinedGoalWeight) || 0) + (Number(form?.combinedFeedbackWeight) || 0);
  const combinedOk = !form?.combinedScoreEnabled || combinedTotal === 100;

  // P1 — likert etiketleri: hepsi boş (varsayılan metin) VEYA 5'i de dolu.
  const likertFilledCount = useMemo(
    () => likertLabels.filter((v) => (v || '').trim() !== '').length,
    [likertLabels],
  );
  const likertOk = likertFilledCount === 0 || likertFilledCount === 5;

  // Faz J (spec §4, J4) — istemci-ipucu (asıl kapı BE:
  // performance.settings.invalidNominationLimits, task-3-report.md §2:
  // 0≤min≤maks≤50). weightsTotal'ın AYNI Number(...)||0 tolerant coercion'ı —
  // form henüz hydrate olmadan (ör. testlerde alan hiç sağlanmadıysa) 0/0
  // sınırda GEÇERLİ kabul edilir (BE'nin kendi sınır kabulüyle TUTARLI).
  const nominationMin = Number(form?.nominationMinTotal) || 0;
  const nominationMax = Number(form?.nominationMaxTotal) || 0;
  const nominationLimitsOk = nominationMin >= 0 && nominationMax >= nominationMin && nominationMax <= 50;

  const canSave = weightsBalanced && combinedOk && likertOk && nominationLimitsOk && insightRulesOk;
  const slaChanged = Boolean(form && settings && SLA_FIELDS.some((k) => Number(form[k]) !== Number(settings[k])));

  const doSave = async (applyToOpenTasks) => {
    const ok = await save({
      ...form,
      likertAnchorLabelsJson: likertLabelsToJson(likertLabels),
      insightRulesJson: insightRulesToJson(insightRules),
      applyToOpenTasks,
    });
    if (ok) {
      setApplySlaToOpenTasks(false);
      setSavedFlash(true);
      setTimeout(() => setSavedFlash(false), 2500);
    }
  };

  // Spec 2026-10-09 §9 — açık görevlere uygulama bilinçli tercih: ConfirmDialog ile onaylanır.
  const handleSave = async () => {
    if (!form || !canSave) return;
    if (slaChanged && applySlaToOpenTasks) {
      setApplyConfirmOpen(true);
      return;
    }
    await doSave(false);
  };

  const tabs = useMemo(() => {
    const list = [
      { key: 'general', label: t('performance.settings.tabGeneral') },
      { key: 'competencies', label: t('performance.competency.title') },
      { key: 'forms', label: t('performance.settings.tabForms') },
      { key: 'notifications', label: t('performance.settings.tabNotifications') },
      { key: 'ratingScales', label: t('performance.settings.tabRatingScales') },
      // Skorlama Faz F, Task 4 (spec §4) — Hedef Şablonları yönetim sekmesi.
      { key: 'goalTemplates', label: t('goalTemplates.tabLabel') },
    ];
    // Faz J (spec 2026-08-08-pm-hijyen-faz-j-design.md §4, J2, Task 4) —
    // yönetici vekâletleri (İK kurar/kapatır). Görev 5 (PMB-03): yalnız
    // enterpriseAdmin — bkz. delegationsVisible.
    if (delegationsVisible) list.push({ key: 'delegations', label: t('delegations.tabLabel') });
    // Webhook yönetimi Public API Plan 03 ile Entegrasyonlar sayfasına (PA modülü) taşındı —
    // webhook artık modüller arasıdır; PM lisansı olmayan kiracı da kendi olaylarına abone olur.
    // Slack Bağlayıcısı (spec 2026-08-10-slack-baglayicisi-design.md §4,
    // Task 4) — dış entegrasyon kartları (v1: yalnız Slack).
    list.push({ key: 'integrations', label: t('integrations.tabLabel') });
    return list;
  }, [t, delegationsVisible]);

  return (
    <Container>
      <Breadcrumbs items={[{ label: t('performance.settings.title') }]} />
      <Tabs tabs={tabs} activeTab={activeTab} onChange={setActiveTab} />

      {activeTab === 'general' && (
        fetching || !form ? (
          <p>{t('common.loading')}</p>
        ) : (
          <>
            <Section>
              <SectionTitle>{t('performance.settings.flags.sectionTitle')}</SectionTitle>
              {/* Müşteri geri bildirimi (2026-10-28): "İK Hedef Onayı'ndaki İK kim, nereden
                  belirleniyor?" — hedef onay zinciri ve İK'nın kaynağı (Rol Yönetimi'ndeki
                  "İK Rolü" işareti; yoksa Şirket Yöneticileri) burada açıkça söylenir. */}
              <HintText>{t('performance.settings.flags.goalApprovalChainHint')}</HintText>
              <FlagsGrid>
                <CheckboxInput
                  label={t('performance.settings.flags.hrGoalApprovalEnabled')}
                  checked={!!form.hrGoalApprovalEnabled}
                  onChange={setBoolField('hrGoalApprovalEnabled')}
                />
                <CheckboxInput
                  label={t('performance.settings.flags.goalRevisionEnabled')}
                  checked={!!form.goalRevisionEnabled}
                  onChange={setBoolField('goalRevisionEnabled')}
                />
                <CheckboxInput
                  label={t('performance.settings.flags.cascadingGoalsEnabled')}
                  checked={!!form.cascadingGoalsEnabled}
                  onChange={setBoolField('cascadingGoalsEnabled')}
                />
                <CheckboxInput
                  label={t('performance.settings.flags.feedbackVisibleInEvaluation')}
                  checked={!!form.feedbackVisibleInEvaluation}
                  onChange={setBoolField('feedbackVisibleInEvaluation')}
                />
                <CheckboxInput
                  label={t('performance.settings.flags.teamFeedbackVisibleToManager')}
                  checked={!!form.teamFeedbackVisibleToManager}
                  onChange={setBoolField('teamFeedbackVisibleToManager')}
                />
                {/* Skorlama Plan 2, Task 12 — dönem sihirbazının (PeriodWizard,
                    Task 11) ön-seçim varsayılanları: kullanıcı sihirbazda
                    dokunmazsa bu iki bayrak + aşağıdaki skala doğrudan
                    kullanılır (bkz. PeriodWizard'ın hydrate useEffect'i). */}
                <CheckboxInput
                  label={t('performance.settings.flags.goalWeightsEnabled')}
                  checked={!!form.goalWeightsEnabled}
                  onChange={setBoolField('goalWeightsEnabled')}
                />
                <CheckboxInput
                  label={t('performance.settings.flags.discloseFinalLevelDefault')}
                  checked={!!form.discloseFinalLevelDefault}
                  onChange={setBoolField('discloseFinalLevelDefault')}
                />
                {/* Faz B (spec 2026-08-07 §2.2, Task 6) — kalibrasyon oturumu
                    sihirbaz ön-seçimi; yukarıdaki iki bayrakla AYNI grup
                    (PeriodWizard'ın 4. adımındaki hydrate useEffect'i tüketir). */}
                <CheckboxInput
                  label={t('performance.settings.flags.calibrationSessionDefault')}
                  checked={!!form.calibrationSessionDefault}
                  onChange={setBoolField('calibrationSessionDefault')}
                />
                {/* Faz J (spec 2026-08-08-pm-hijyen-faz-j-design.md §4, J3,
                    Task 4) — açıkken üst yöneticiler TeamGoals'ta TÜM alt
                    ağacı (recursive) salt-okunur görür ("Alt ekip" rozeti);
                    karar yetkisi her zaman doğrudan yöneticide kalır. */}
                <CheckboxInput
                  label={t('performance.settings.flags.skipLevelVisibilityEnabled')}
                  checked={!!form.skipLevelVisibilityEnabled}
                  onChange={setBoolField('skipLevelVisibilityEnabled')}
                />
              </FlagsGrid>
            </Section>

            {/* Skorlama Plan 2, Task 12 — dönem sihirbazının Sistem B adımında
                ön-seçilen varsayılan puan skalası (BE: DefaultRatingScaleId,
                nullable — geçersiz id `performance.settings
                .invalidDefaultRatingScale` ile reddedilir, boş bırakmak
                serbest). Kendi mini bölümü: ne "Bayraklar" (metin/toggle
                değil, ilişkisel bir seçim) ne "Ağırlıklar" (yüzde alanı değil)
                grubuna doğal olarak uymuyor. */}
            <Section>
              <SectionTitle>{t('performance.settings.defaultScale.sectionTitle')}</SectionTitle>
              <HintText>{t('performance.settings.defaultScale.hint')}</HintText>
              <FieldsGrid>
                <GenericSelect
                  label={t('performance.settings.defaultScale.label')}
                  value={form.defaultRatingScaleId ?? ''}
                  onChange={(v) => setForm((prev) => ({ ...prev, defaultRatingScaleId: v || null }))}
                  options={scaleOptions}
                />
              </FieldsGrid>
            </Section>

            <Section>
              <SectionTitle>{t('performance.settings.weights.sectionTitle')}</SectionTitle>
              <HintText>{t('performance.settings.weights.hint')}</HintText>
              <FieldsGrid>
                <TextInput label={t('performance.settings.weights.self')} type="number" value={form.weightSelf} onChange={setNumField('weightSelf')} />
                <TextInput label={t('performance.settings.weights.manager')} type="number" value={form.weightManager} onChange={setNumField('weightManager')} />
                <TextInput label={t('performance.settings.weights.peer')} type="number" value={form.weightPeer} onChange={setNumField('weightPeer')} />
                <TextInput label={t('performance.settings.weights.subordinate')} type="number" value={form.weightSubordinate} onChange={setNumField('weightSubordinate')} />
                <TextInput label={t('performance.settings.weights.senior')} type="number" value={form.weightSenior} onChange={setNumField('weightSenior')} />
              </FieldsGrid>
              <TotalRow>
                <TotalWeight $balanced={weightsBalanced}>
                  {t('performance.settings.weights.total')}: {weightsTotal}% / 100%
                </TotalWeight>
              </TotalRow>
            </Section>

            <Section>
              <SectionTitle>{t('performance.settings.thresholds.sectionTitle')}</SectionTitle>
              <FieldsGrid>
                <TextInput label={t('performance.settings.thresholds.anonymityThreshold')} type="number" value={form.anonymityThreshold} onChange={setNumField('anonymityThreshold')} />
                <TextInput label={t('performance.settings.thresholds.newEmployeeInclusionThresholdPct')} type="number" value={form.newEmployeeInclusionThresholdPct} onChange={setNumField('newEmployeeInclusionThresholdPct')} />
                <TextInput label={t('performance.settings.thresholds.kvkkRetentionYears')} type="number" value={form.kvkkRetentionYears} onChange={setNumField('kvkkRetentionYears')} />
                <TextInput label={t('performance.settings.thresholds.objectionWindowBusinessDays')} type="number" min={1} max={30} value={form.objectionWindowBusinessDays} onChange={setNumField('objectionWindowBusinessDays')} />
                {/* Skorlama Faz F, Task 5 (spec §4, task-3-report.md §1.2) —
                    0-365, 0=kapalı (hata DEĞİL — geçerli bir "bayatlık
                    uyarısını kapat" durumu). */}
                <TextInput
                  data-role="stale-goal-days"
                  label={t('performance.settings.thresholds.staleGoalDays')}
                  type="number"
                  min={0}
                  max={365}
                  value={form.staleGoalDays}
                  onChange={setNumField('staleGoalDays')}
                />
                {/* Faz J (spec §4, J4, Task 4) — 360° değerlendirici aday
                    sayısı sınırları (task-3-report.md §2: 0≤min≤maks≤50). */}
                <TextInput
                  data-role="nomination-min-total"
                  label={t('performance.settings.thresholds.nominationMinTotal')}
                  type="number"
                  min={0}
                  max={50}
                  value={form.nominationMinTotal}
                  onChange={setNumField('nominationMinTotal')}
                />
                <TextInput
                  data-role="nomination-max-total"
                  label={t('performance.settings.thresholds.nominationMaxTotal')}
                  type="number"
                  min={0}
                  max={50}
                  value={form.nominationMaxTotal}
                  onChange={setNumField('nominationMaxTotal')}
                />
              </FieldsGrid>
              <HintText>{t('performance.settings.thresholds.staleGoalDaysHint')}</HintText>
              <HintText>{t('performance.settings.thresholds.nominationLimitsHint')}</HintText>
              {!nominationLimitsOk && (
                <TotalRow>
                  <TotalWeight $balanced={false}>
                    {t('performance.settings.thresholds.nominationLimitsInvalidHint')}
                  </TotalWeight>
                </TotalRow>
              )}
            </Section>

            <Section data-role="insight-rules">
              <SectionTitle>{t('performance.settings.insightRules.sectionTitle')}</SectionTitle>
              <FieldsGrid>
                <TextInput label={t('performance.settings.insightRules.overdueTaskSharePct')} type="number" min={1} max={100} value={insightRules.overdueTaskSharePct} onChange={setInsightField('overdueTaskSharePct')} />
                <TextInput label={t('performance.settings.insightRules.overdueAssigneeSharePct')} type="number" min={1} max={100} value={insightRules.overdueAssigneeSharePct} onChange={setInsightField('overdueAssigneeSharePct')} />
                <TextInput label={t('performance.settings.insightRules.overdueMinTasks')} type="number" min={1} value={insightRules.overdueMinTasks} onChange={setInsightField('overdueMinTasks')} />
                <TextInput label={t('performance.settings.insightRules.slowdownIncreasePct')} type="number" min={1} max={900} value={insightRules.slowdownIncreasePct} onChange={setInsightField('slowdownIncreasePct')} />
                <TextInput label={t('performance.settings.insightRules.slowdownWindowDays')} type="number" min={1} max={90} value={insightRules.slowdownWindowDays} onChange={setInsightField('slowdownWindowDays')} />
                <TextInput label={t('performance.settings.insightRules.slowdownMinN')} type="number" min={1} value={insightRules.slowdownMinN} onChange={setInsightField('slowdownMinN')} />
                <TextInput label={t('performance.settings.insightRules.reviewScoreBelow')} type="number" min={0} max={100} value={insightRules.reviewScoreBelow} onChange={setInsightField('reviewScoreBelow')} />
                <TextInput label={t('performance.settings.insightRules.pileupWindowDays')} type="number" min={1} max={30} value={insightRules.pileupWindowDays} onChange={setInsightField('pileupWindowDays')} />
                <TextInput label={t('performance.settings.insightRules.pileupMinTasks')} type="number" min={1} value={insightRules.pileupMinTasks} onChange={setInsightField('pileupMinTasks')} />
                <TextInput label={t('performance.settings.insightRules.pileupUntouchedSharePct')} type="number" min={1} max={100} value={insightRules.pileupUntouchedSharePct} onChange={setInsightField('pileupUntouchedSharePct')} />
                <TextInput label={t('performance.settings.insightRules.pileupRedSharePct')} type="number" min={1} max={100} value={insightRules.pileupRedSharePct} onChange={setInsightField('pileupRedSharePct')} />
              </FieldsGrid>
              <HintText>{t('performance.settings.insightRules.hint')}</HintText>
            </Section>

            <Section data-role="insight-ai-rules">
              <SectionTitle>{t('performance.settings.insightRules.aiSectionTitle')}</SectionTitle>
              <FieldsGrid>
                <TextInput label={t('performance.settings.insightRules.goalDeclinePp')} type="number" min={1} max={100} value={insightRules.goalDeclinePp} onChange={setInsightField('goalDeclinePp')} />
                <TextInput label={t('performance.settings.insightRules.goalDeclineMinN')} type="number" min={1} value={insightRules.goalDeclineMinN} onChange={setInsightField('goalDeclineMinN')} />
                <TextInput label={t('performance.settings.insightRules.goalDeclineMinCoveragePct')} type="number" min={1} max={100} value={insightRules.goalDeclineMinCoveragePct} onChange={setInsightField('goalDeclineMinCoveragePct')} />
                <TextInput label={t('performance.settings.insightRules.reviewGapT')} type="number" min={0.1} max={4} step={0.1} value={insightRules.reviewGapT} onChange={setInsightField('reviewGapT')} />
                <TextInput label={t('performance.settings.insightRules.deviationAbs')} type="number" min={0.1} max={4} step={0.1} value={insightRules.deviationAbs} onChange={setInsightField('deviationAbs')} />
                <TextInput label={t('performance.settings.insightRules.deviationManagerMinN')} type="number" min={1} value={insightRules.deviationManagerMinN} onChange={setInsightField('deviationManagerMinN')} />
                <TextInput label={t('performance.settings.insightRules.deviationPeerMinN')} type="number" min={1} value={insightRules.deviationPeerMinN} onChange={setInsightField('deviationPeerMinN')} />
                <TextInput label={t('performance.settings.insightRules.deviationMinCoveragePct')} type="number" min={1} max={100} value={insightRules.deviationMinCoveragePct} onChange={setInsightField('deviationMinCoveragePct')} />
                <TextInput label={t('performance.settings.insightRules.competencyGapPeriods')} type="number" min={2} max={6} value={insightRules.competencyGapPeriods} onChange={setInsightField('competencyGapPeriods')} />
              </FieldsGrid>
              <HintText>{t('performance.settings.insightRules.aiHint')}</HintText>
              {!insightRulesOk && (
                <TotalRow>
                  <TotalWeight $balanced={false}>{t('performance.settings.insightRules.invalidHint')}</TotalWeight>
                </TotalRow>
              )}
            </Section>

            <Section>
              <SectionTitle>{t('performance.settings.sla.sectionTitle')}</SectionTitle>
              <FieldsGrid>
                <TextInput label={t('performance.settings.sla.goalWritingDays')} type="number" value={form.slaGoalWritingDays} onChange={setNumField('slaGoalWritingDays')} />
                <TextInput label={t('performance.settings.sla.managerApprovalDays')} type="number" value={form.slaManagerApprovalDays} onChange={setNumField('slaManagerApprovalDays')} />
                <TextInput label={t('performance.settings.sla.hrApprovalDays')} type="number" value={form.slaHrApprovalDays} onChange={setNumField('slaHrApprovalDays')} />
                <TextInput label={t('performance.settings.sla.nominationDays')} type="number" value={form.slaNominationDays} onChange={setNumField('slaNominationDays')} />
                <TextInput label={t('performance.settings.sla.nominationApprovalDays')} type="number" value={form.slaNominationApprovalDays} onChange={setNumField('slaNominationApprovalDays')} />
                <TextInput data-role="sla-feedback-fill-days" label={t('performance.settings.sla.feedbackFillDays')} type="number" value={form.slaFeedbackFillDays} onChange={setNumField('slaFeedbackFillDays')} />
                <TextInput
                  data-role="eval-close-warning-days"
                  label={t('performance.settings.sla.closeWarningDays')}
                  type="number"
                  min={1}
                  max={30}
                  value={form.evalCloseWarningDays}
                  onChange={setNumField('evalCloseWarningDays')}
                />
              </FieldsGrid>
              {slaChanged && (
                <>
                  <CheckboxInput
                    label={t('performance.settings.sla.applyToOpenTasks')}
                    checked={applySlaToOpenTasks}
                    onChange={setApplySlaToOpenTasks}
                  />
                  <HintText>{t('performance.settings.sla.applyToOpenTasksHint')}</HintText>
                </>
              )}
            </Section>

            <Section>
              <SectionTitle>{t('performance.settings.quietHours.sectionTitle')}</SectionTitle>
              <HintText>{t('performance.settings.quietHours.hint')}</HintText>
              <FieldsGrid>
                <TimeInput label={t('performance.settings.quietHours.start')} value={form.quietHoursStart} onChange={setTimeField('quietHoursStart')} />
                <TimeInput label={t('performance.settings.quietHours.end')} value={form.quietHoursEnd} onChange={setTimeField('quietHoursEnd')} />
                <GenericSelect
                  label={t('performance.settings.quietHours.timeZone')}
                  value={form.timeZoneId || DEFAULT_TIME_ZONE_ID}
                  onChange={(v) => setForm((prev) => ({ ...prev, timeZoneId: v }))}
                  options={tzOptions}
                />
              </FieldsGrid>
            </Section>

            {/* P1 — birleşik A+B genel skoru (bayrak arkasında; ağırlık toplam
                sayacı 360 ağırlık bölümündeki desenin aynısı). */}
            <Section>
              <SectionTitle>{t('performance.settings.combined.sectionTitle')}</SectionTitle>
              <HintText>{t('performance.settings.combined.hint')}</HintText>
              <FlagsGrid>
                <CheckboxInput
                  label={t('performance.settings.combined.enabled')}
                  checked={!!form.combinedScoreEnabled}
                  onChange={setBoolField('combinedScoreEnabled')}
                />
              </FlagsGrid>
              {form.combinedScoreEnabled && (
                <>
                  <FieldsGrid style={{ marginTop: 14 }}>
                    <TextInput label={t('performance.settings.combined.goalWeight')} type="number" value={form.combinedGoalWeight} onChange={setNumField('combinedGoalWeight')} />
                    <TextInput label={t('performance.settings.combined.feedbackWeight')} type="number" value={form.combinedFeedbackWeight} onChange={setNumField('combinedFeedbackWeight')} />
                  </FieldsGrid>
                  <TotalRow>
                    <TotalWeight $balanced={combinedOk}>
                      {t('performance.settings.weights.total')}: {combinedTotal}% / 100%
                    </TotalWeight>
                  </TotalRow>
                </>
              )}
            </Section>

            {/* P1 — 360 likert çapa etiketleri: hepsi boş → varsayılan ölçek
                metni; doluysa 5'i birden zorunlu (BE sözleşmesi). */}
            <Section>
              <SectionTitle>{t('performance.settings.likert.sectionTitle')}</SectionTitle>
              <HintText>{t('performance.settings.likert.hint')}</HintText>
              <FieldsGrid>
                {LIKERT_SCORES.map((score, idx) => (
                  <TextInput
                    key={score}
                    label={t('performance.settings.likert.labelForScore', { score })}
                    value={likertLabels[idx]}
                    onChange={(e) => setLikertLabels((prev) => {
                      const next = [...prev];
                      next[idx] = e.target.value;
                      return next;
                    })}
                  />
                ))}
              </FieldsGrid>
              {!likertOk && (
                <TotalRow>
                  <TotalWeight $balanced={false}>{t('performance.settings.likert.incompleteHint')}</TotalWeight>
                </TotalRow>
              )}
            </Section>

            <FooterBar>
              {savedFlash && <SavedHint>{t('performance.settings.savedHint')}</SavedHint>}
              <Button onClick={handleSave} disabled={saving || !canSave} loading={saving}>
                {saving ? t('common.saving') : t('common.save')}
              </Button>
            </FooterBar>
          </>
        )
      )}

      {activeTab === 'competencies' && <CompetenciesTab />}
      {activeTab === 'forms' && <FeedbackFormsTab />}
      {activeTab === 'notifications' && <NotificationTemplatesTab />}
      {activeTab === 'ratingScales' && <RatingScalesTab />}
      {activeTab === 'goalTemplates' && <GoalTemplatesTab />}
      {activeTab === 'delegations' && delegationsVisible && <DelegationsTab />}
      {activeTab === 'integrations' && <IntegrationsTab />}
      <ConfirmDialog
        isOpen={applyConfirmOpen}
        title={t('performance.settings.sla.applyConfirmTitle')}
        message={t('performance.settings.sla.applyConfirmMessage')}
        onConfirm={async () => { setApplyConfirmOpen(false); await doSave(true); }}
        onCancel={() => setApplyConfirmOpen(false)}
        loading={saving}
        type="info"
      />
    </Container>
  );
}
